import chokidar, { FSWatcher } from 'chokidar';
import { access, readdir, stat } from 'fs/promises';
import { join, relative, dirname } from 'path';
import chalk from 'chalk';
import { calculateFileHash } from '../utils/file-hash.js';
import { IgnoreMatcher } from '../utils/ignore-matcher.js';
import { loadConfig } from '../utils/config-loader.js';
import { convertFromMemory, convertToMemory, detectFormat, getOutputFilename } from '../converters/index.js';
import { Config, FileInfo, SourceFormat, TargetFormat } from '../types/index.js';
import { success, warning, error, info, select } from '../utils/prompt.js';

/**
 * Format a date object to a readable string
 */
function formatDate(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * File watcher for auto-syncing memory files
 */
export class MemoryWatcher {
  private basePath: string;
  private config: Config;
  private watcher?: FSWatcher;
  private fileHashes: Map<string, string> = new Map();
  private ignoreMatcher?: IgnoreMatcher;
  private isProcessing = false;
  private changeQueue: Set<string> = new Set();
  private syncingFiles: Set<string> = new Set(); // Track files being synced to avoid false conflicts

  constructor(basePath: string) {
    this.basePath = basePath;
    this.config = {} as Config; // Will be loaded in initialize()
  }

  /**
   * Initialize watcher
   */
  async initialize(): Promise<void> {
    // Load configuration
    this.config = await loadConfig(this.basePath);

    // Initialize ignore matcher
    this.ignoreMatcher = new IgnoreMatcher(this.basePath, this.config);
    await this.ignoreMatcher.initialize();

    // Scan for existing files
    await this.scanAndAutoComplete();

    console.log(chalk.green('✓'), 'Watcher initialized\n');
  }

  /**
   * Start watching for changes
   */
  async watch(): Promise<void> {
    const patterns = ['**/MEMORY.md', '**/CLAUDE.md', '**/AGENTS.md'];

    this.watcher = chokidar.watch(patterns, {
      cwd: this.basePath,
      persistent: true,
      ignoreInitial: true,
      awaitWriteFinish: {
        stabilityThreshold: 300,
        pollInterval: 100,
      },
    });

    this.watcher.on('change', async (path) => {
      await this.handleChange(join(this.basePath, path));
    });

    this.watcher.on('add', async (path) => {
      await this.handleAdd(join(this.basePath, path));
    });

    this.watcher.on('unlink', async (path) => {
      await this.handleUnlink(join(this.basePath, path));
    });

    console.log(chalk.dim('Press Ctrl+C to stop watching\n'));
  }

  /**
   * Stop watching
   */
  async stop(): Promise<void> {
    if (this.watcher) {
      await this.watcher.close();
    }
  }

  /**
   * Scan directory and auto-complete missing files
   */
  private async scanAndAutoComplete(): Promise<void> {
    const directories = await this.findDirectoriesWithMemoryFiles();

    for (const dir of directories) {
      const relativeDir = relative(this.basePath, dir);
      const displayDir = relativeDir || '.';

      const memoryPath = join(dir, 'MEMORY.md');
      const claudePath = join(dir, 'CLAUDE.md');
      const agentsPath = join(dir, 'AGENTS.md');

      const hasMemory = await fileExists(memoryPath);
      const hasClaude = await fileExists(claudePath);
      const hasAgents = await fileExists(agentsPath);

      // Calculate hashes for existing files
      if (hasMemory) {
        this.fileHashes.set(memoryPath, await calculateFileHash(memoryPath));
      }
      if (hasClaude) {
        this.fileHashes.set(claudePath, await calculateFileHash(claudePath));
      }
      if (hasAgents) {
        this.fileHashes.set(agentsPath, await calculateFileHash(agentsPath));
      }

      // Auto-complete missing files
      if (!hasMemory && (hasClaude || hasAgents)) {
        if (this.config.watch.autoConvert) {
          // Auto-convert silently
          await this.autoImportToMemory(dir, hasClaude, hasAgents);
        }
      }

      // Generate missing compiled files if MEMORY.md exists
      if (hasMemory) {
        if (!hasClaude || !hasAgents) {
          await this.compileFromMemory(memoryPath, !hasClaude, !hasAgents);
        } else {
          // All three files exist - check if they're in sync
          await this.checkAndSyncFiles(memoryPath, claudePath, agentsPath);
        }
      }

      // Log discovered files
      if (hasMemory || hasClaude || hasAgents) {
        info(`Found in ${chalk.cyan(displayDir)}:`);
        if (hasMemory) console.log(chalk.dim('  ✓ MEMORY.md'));
        if (hasClaude) console.log(chalk.dim('  ✓ CLAUDE.md'));
        if (hasAgents) console.log(chalk.dim('  ✓ AGENTS.md'));
      }
    }
  }

  /**
   * Auto-import CLAUDE.md or AGENTS.md to MEMORY.md
   */
  private async autoImportToMemory(
    dir: string,
    hasClaude: boolean,
    hasAgents: boolean
  ): Promise<void> {
    const memoryPath = join(dir, 'MEMORY.md');

    // Priority: CLAUDE.md > AGENTS.md
    const sourceFile = hasClaude ? join(dir, 'CLAUDE.md') : join(dir, 'AGENTS.md');
    const sourceFormat: SourceFormat = hasClaude ? 'CLAUDE.md' : 'AGENTS.md';

    try {
      await convertToMemory(sourceFile, memoryPath, sourceFormat);
      this.fileHashes.set(memoryPath, await calculateFileHash(memoryPath));

      success(`Generated MEMORY.md from ${sourceFormat} in ${relative(this.basePath, dir) || '.'}`);

      // Also generate the other compiled file
      await this.compileFromMemory(memoryPath, !hasClaude, !hasAgents);
    } catch (err) {
      error(`Failed to import ${sourceFormat}: ${(err as Error).message}`);
    }
  }

  /**
   * Compile MEMORY.md to missing targets
   */
  private async compileFromMemory(
    memoryPath: string,
    needClaude: boolean,
    needAgents: boolean
  ): Promise<void> {
    const dir = dirname(memoryPath);

    if (needClaude) {
      const claudePath = join(dir, 'CLAUDE.md');
      try {
        await convertFromMemory(memoryPath, claudePath, 'CLAUDE.md', this.config.compile);
        this.fileHashes.set(claudePath, await calculateFileHash(claudePath));
      } catch (err) {
        error(`Failed to generate CLAUDE.md: ${(err as Error).message}`);
      }
    }

    if (needAgents) {
      const agentsPath = join(dir, 'AGENTS.md');
      try {
        await convertFromMemory(memoryPath, agentsPath, 'AGENTS.md', this.config.compile);
        this.fileHashes.set(agentsPath, await calculateFileHash(agentsPath));
      } catch (err) {
        error(`Failed to generate AGENTS.md: ${(err as Error).message}`);
      }
    }
  }

  /**
   * Check if files are in sync and sync them if needed
   */
  private async checkAndSyncFiles(
    memoryPath: string,
    claudePath: string,
    agentsPath: string
  ): Promise<void> {
    const memoryHash = this.fileHashes.get(memoryPath)!;
    const claudeHash = this.fileHashes.get(claudePath)!;
    const agentsHash = this.fileHashes.get(agentsPath)!;

    // Count unique hashes
    const uniqueHashes = new Set([memoryHash, claudeHash, agentsHash]);

    if (uniqueHashes.size === 1) {
      // All files are in sync
      return;
    }

    // Get modification times
    const memoryStat = await stat(memoryPath);
    const claudeStat = await stat(claudePath);
    const agentsStat = await stat(agentsPath);

    const memoryTime = memoryStat.mtime.getTime();
    const claudeTime = claudeStat.mtime.getTime();
    const agentsTime = agentsStat.mtime.getTime();

    if (uniqueHashes.size === 3) {
      // All three files have different content - this is a conflict!
      console.log();
      warning('Conflict detected on startup! All three files have different content.');
      console.log(chalk.dim(`  - MEMORY.md (modified: ${formatDate(memoryStat.mtime)})`));
      console.log(chalk.dim(`  - CLAUDE.md (modified: ${formatDate(claudeStat.mtime)})`));
      console.log(chalk.dim(`  - AGENTS.md (modified: ${formatDate(agentsStat.mtime)})`));
      console.log();

      if (this.config.watch.onConflict === 'ask') {
        const choice = await select(
          'Which file should be used as source?',
          [
            'Use MEMORY.md',
            'Use CLAUDE.md',
            'Use AGENTS.md',
            'Skip (manual resolution)',
          ],
          'Use MEMORY.md'
        );

        if (choice === 'Use MEMORY.md') {
          await this.syncFromMemory(memoryPath);
        } else if (choice === 'Use CLAUDE.md') {
          await this.syncToMemory(claudePath, 'CLAUDE.md' as SourceFormat);
        } else if (choice === 'Use AGENTS.md') {
          await this.syncToMemory(agentsPath, 'AGENTS.md' as SourceFormat);
        } else {
          info('Skipped auto-sync. Resolve conflicts manually before making changes.');
        }
      } else if (this.config.watch.onConflict === 'memory-priority') {
        warning('Using MEMORY.md as source (memory-priority mode)');
        await this.syncFromMemory(memoryPath);
      } else {
        warning('Auto-sync skipped. Resolve conflicts manually.');
      }
      return;
    }

    // Two files have same content, one is different
    // Determine which file is the outlier and if it's the newest

    if (memoryHash === claudeHash) {
      // MEMORY and CLAUDE match, AGENTS is different
      // Use AGENTS only if it's newer than BOTH others
      if (agentsTime > memoryTime && agentsTime > claudeTime) {
        warning(`Files out of sync. AGENTS.md is newest - using it as source.`);
        await this.syncToMemory(agentsPath, 'AGENTS.md' as SourceFormat);
      } else {
        warning(`Files out of sync. MEMORY.md/CLAUDE.md are in sync - using them as source.`);
        await this.syncFromMemory(memoryPath);
      }
    } else if (memoryHash === agentsHash) {
      // MEMORY and AGENTS match, CLAUDE is different
      // Use CLAUDE only if it's newer than BOTH others
      if (claudeTime > memoryTime && claudeTime > agentsTime) {
        warning(`Files out of sync. CLAUDE.md is newest - using it as source.`);
        await this.syncToMemory(claudePath, 'CLAUDE.md' as SourceFormat);
      } else {
        warning(`Files out of sync. MEMORY.md/AGENTS.md are in sync - using them as source.`);
        await this.syncFromMemory(memoryPath);
      }
    } else {
      // claudeHash === agentsHash, MEMORY is different
      // Use MEMORY only if it's newer than BOTH others
      if (memoryTime > claudeTime && memoryTime > agentsTime) {
        warning(`Files out of sync. MEMORY.md is newest - using it as source.`);
        await this.syncFromMemory(memoryPath);
      } else {
        warning(`Files out of sync. CLAUDE.md/AGENTS.md are in sync - using them as source.`);
        await this.syncToMemory(claudePath, 'CLAUDE.md' as SourceFormat);
      }
    }
  }

  /**
   * Handle file change event
   */
  private async handleChange(filePath: string): Promise<void> {
    // Check if should ignore
    const relativePath = relative(this.basePath, filePath);
    if (this.ignoreMatcher?.shouldIgnore(relativePath)) {
      return;
    }

    // Skip if this file is currently being synced (to avoid false conflicts)
    if (this.syncingFiles.has(filePath)) {
      return;
    }

    // Add to queue
    this.changeQueue.add(filePath);

    // Process queue after a short delay (debounce)
    setTimeout(() => this.processChangeQueue(), 500);
  }

  /**
   * Process queued changes
   */
  private async processChangeQueue(): Promise<void> {
    if (this.isProcessing || this.changeQueue.size === 0) {
      return;
    }

    this.isProcessing = true;
    const changedFiles = Array.from(this.changeQueue);
    this.changeQueue.clear();

    // Check for conflicts (multiple files changed)
    if (changedFiles.length > 1) {
      // Check if changes are actually different or just sync updates
      const hasRealConflict = await this.detectRealConflict(changedFiles);

      if (hasRealConflict) {
        await this.handleConflict(changedFiles);
      } else {
        // All files have consistent content, process normally
        // Prioritize MEMORY.md if it's in the list
        const memoryFile = changedFiles.find(f => f.endsWith('MEMORY.md'));
        if (memoryFile) {
          await this.processChange(memoryFile);
        } else {
          // Process the first changed file
          await this.processChange(changedFiles[0]);
        }
      }
    } else {
      await this.processChange(changedFiles[0]);
    }

    this.isProcessing = false;
  }

  /**
   * Process a single file change
   */
  private async processChange(filePath: string): Promise<void> {
    const newHash = await calculateFileHash(filePath);
    const oldHash = this.fileHashes.get(filePath);

    if (newHash === oldHash) {
      return; // No actual change
    }

    const filename = filePath.split('/').pop()!;
    const dir = dirname(filePath);
    const relativeDir = relative(this.basePath, dir) || '.';

    info(`${chalk.cyan(filename)} changed in ${chalk.dim(relativeDir)}`);

    // Update hash BEFORE syncing to prevent re-triggering
    this.fileHashes.set(filePath, newHash);

    if (filename === 'MEMORY.md') {
      // MEMORY.md changed → recompile to CLAUDE.md and AGENTS.md
      await this.syncFromMemory(filePath);
    } else if (filename === 'CLAUDE.md' || filename === 'AGENTS.md') {
      // Compiled file changed → update MEMORY.md
      await this.syncToMemory(filePath, filename as SourceFormat);
    }
  }

  /**
   * Sync changes from MEMORY.md to compiled files
   */
  private async syncFromMemory(memoryPath: string): Promise<void> {
    const dir = dirname(memoryPath);
    const claudePath = join(dir, 'CLAUDE.md');
    const agentsPath = join(dir, 'AGENTS.md');

    // Mark files as syncing
    this.syncingFiles.add(claudePath);
    this.syncingFiles.add(agentsPath);

    try {
      // Compile to CLAUDE.md
      try {
        await convertFromMemory(memoryPath, claudePath, 'CLAUDE.md', this.config.compile);
        this.fileHashes.set(claudePath, await calculateFileHash(claudePath));
        success(`  Updated CLAUDE.md`);
      } catch (err) {
        error(`  Failed to update CLAUDE.md: ${(err as Error).message}`);
      }

      // Compile to AGENTS.md
      try {
        await convertFromMemory(memoryPath, agentsPath, 'AGENTS.md', this.config.compile);
        this.fileHashes.set(agentsPath, await calculateFileHash(agentsPath));
        success(`  Updated AGENTS.md`);
      } catch (err) {
        error(`  Failed to update AGENTS.md: ${(err as Error).message}`);
      }
    } finally {
      // Clear syncing flags after a delay to ensure file watcher events are processed
      setTimeout(() => {
        this.syncingFiles.delete(claudePath);
        this.syncingFiles.delete(agentsPath);
      }, 1000);
    }
  }

  /**
   * Sync changes from compiled file to MEMORY.md
   */
  private async syncToMemory(filePath: string, sourceFormat: SourceFormat): Promise<void> {
    const dir = dirname(filePath);
    const memoryPath = join(dir, 'MEMORY.md');
    const otherFormat = sourceFormat === 'CLAUDE.md' ? 'AGENTS.md' : 'CLAUDE.md';
    const otherPath = join(dir, otherFormat);

    // Mark files as syncing
    this.syncingFiles.add(memoryPath);
    this.syncingFiles.add(otherPath);

    try {
      await convertToMemory(filePath, memoryPath, sourceFormat);
      this.fileHashes.set(memoryPath, await calculateFileHash(memoryPath));
      success(`  Updated MEMORY.md`);

      // Also update the other compiled file
      await convertFromMemory(memoryPath, otherPath, otherFormat as TargetFormat, this.config.compile);
      this.fileHashes.set(otherPath, await calculateFileHash(otherPath));
      success(`  Updated ${otherFormat}`);
    } catch (err) {
      error(`  Failed to sync: ${(err as Error).message}`);
    } finally {
      // Clear syncing flags after a delay
      setTimeout(() => {
        this.syncingFiles.delete(memoryPath);
        this.syncingFiles.delete(otherPath);
      }, 1000);
    }
  }

  /**
   * Detect if there's a real conflict or just sync updates
   * Returns true if files have different content that conflicts
   */
  private async detectRealConflict(files: string[]): Promise<boolean> {
    const dir = dirname(files[0]);
    const memoryPath = join(dir, 'MEMORY.md');
    const claudePath = join(dir, 'CLAUDE.md');
    const agentsPath = join(dir, 'AGENTS.md');

    // Group files by directory
    const filesByDir = new Map<string, string[]>();
    for (const file of files) {
      const fileDir = dirname(file);
      if (!filesByDir.has(fileDir)) {
        filesByDir.set(fileDir, []);
      }
      filesByDir.get(fileDir)!.push(file);
    }

    // Check each directory
    for (const [dir, dirFiles] of filesByDir.entries()) {
      const hasMemory = dirFiles.some(f => f.endsWith('MEMORY.md'));
      const hasClaude = dirFiles.some(f => f.endsWith('CLAUDE.md'));
      const hasAgents = dirFiles.some(f => f.endsWith('AGENTS.md'));

      // If MEMORY.md changed along with compiled files, it's likely a sync, not a conflict
      if (hasMemory && (hasClaude || hasAgents)) {
        // Check if MEMORY.md is the source of the changes
        const memPath = join(dir, 'MEMORY.md');
        const memHash = await calculateFileHash(memPath);
        const oldMemHash = this.fileHashes.get(memPath);

        // If MEMORY.md actually changed, compiled files are just sync updates
        if (memHash !== oldMemHash) {
          return false; // Not a real conflict, just sync
        }
      }

      // If only compiled files changed (CLAUDE and AGENTS), check if they have same content
      if (!hasMemory && hasClaude && hasAgents) {
        const cPath = join(dir, 'CLAUDE.md');
        const aPath = join(dir, 'AGENTS.md');

        try {
          const claudeHash = await calculateFileHash(cPath);
          const agentsHash = await calculateFileHash(aPath);

          // If both have same hash, they have identical content - no conflict
          if (claudeHash === agentsHash) {
            return false;
          }
        } catch {
          // If we can't read files, assume there's a conflict
          return true;
        }
      }
    }

    // If we have files from multiple directories, it's a real multi-directory change
    if (filesByDir.size > 1) {
      return false; // Process separately, not a conflict
    }

    // Default: assume there's a conflict if multiple files changed in unexpected ways
    return true;
  }

  /**
   * Handle conflict when multiple files changed
   */
  private async handleConflict(files: string[]): Promise<void> {
    console.log();
    warning('Conflict detected! Multiple files changed:');
    files.forEach((f) => console.log(chalk.dim(`  - ${relative(this.basePath, f)}`)));
    console.log();

    if (this.config.watch.onConflict === 'ask') {
      const choice = await select(
        'How to resolve?',
        [
          'Use MEMORY.md as source',
          'Merge changes',
          'Skip this sync',
        ],
        'Use MEMORY.md as source'
      );

      if (choice === 'Use MEMORY.md as source') {
        const memoryFile = files.find((f) => f.endsWith('MEMORY.md'));
        if (memoryFile) {
          await this.processChange(memoryFile);
        }
      } else if (choice === 'Skip this sync') {
        info('Skipped sync');
      } else {
        warning('Merge not implemented yet. Skipping...');
      }

      console.log();
      info('Configure conflict behavior in .mem2rc.json (watch.onConflict)');
    } else if (this.config.watch.onConflict === 'memory-priority') {
      const memoryFile = files.find((f) => f.endsWith('MEMORY.md'));
      if (memoryFile) {
        await this.processChange(memoryFile);
      }
    } else {
      info('Watch paused due to conflict');
      info('Resolve manually and restart watch mode');
      await this.stop();
    }
  }

  /**
   * Handle file add event
   */
  private async handleAdd(filePath: string): Promise<void> {
    await this.handleChange(filePath);
  }

  /**
   * Handle file delete event
   */
  private async handleUnlink(filePath: string): Promise<void> {
    this.fileHashes.delete(filePath);
    const relativePath = relative(this.basePath, filePath);
    info(`${chalk.cyan(relativePath)} was deleted`);
  }

  /**
   * Find all directories containing memory files
   */
  private async findDirectoriesWithMemoryFiles(): Promise<string[]> {
    const result: string[] = [];

    async function scan(dir: string, ignoreMatcher?: IgnoreMatcher): Promise<void> {
      const entries = await readdir(dir, { withFileTypes: true });

      let hasMemoryFile = false;
      for (const entry of entries) {
        if (entry.isFile()) {
          const name = entry.name;
          if (name === 'MEMORY.md' || name === 'CLAUDE.md' || name === 'AGENTS.md') {
            hasMemoryFile = true;
            break;
          }
        }
      }

      if (hasMemoryFile) {
        result.push(dir);
      }

      // Recursively scan subdirectories
      for (const entry of entries) {
        if (entry.isDirectory()) {
          const subdir = join(dir, entry.name);
          const relativePath = relative(process.cwd(), subdir);

          // Check if should ignore
          if (ignoreMatcher?.shouldIgnore(relativePath)) {
            continue;
          }

          await scan(subdir, ignoreMatcher);
        }
      }
    }

    await scan(this.basePath, this.ignoreMatcher);
    return result;
  }
}

/**
 * Check if file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}
